'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import { Label } from '@/shared/components/Label';
import { useAuth } from '@/features/auth';
import {
	registerSchema,
	type RegisterFormData,
} from '@/features/auth/validation/schemas';
import { cn } from '@/shared/lib/utils';
import { logger } from '@/core/logger';

interface RegisterFormProps {
	className?: string;
	defaultEmail?: string;
}

export function RegisterForm({ className, defaultEmail }: RegisterFormProps) {
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [error, setError] = useState<string>('');
	const { signUp } = useAuth();
	const router = useRouter();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<RegisterFormData>({
		resolver: zodResolver(registerSchema),
		defaultValues: {
			email: defaultEmail ?? '',
		},
	});

	const onSubmit = async (data: RegisterFormData) => {
		try {
			setError('');
			await signUp(data);
			logger.info('Registration successful');
			router.push('/email/verify');
		} catch (error) {
			// Server action error already logged on server with appropriate level
			// Log user action outcome for client-side monitoring
			logger.warn('Registration attempt failed');
			setError(
				error instanceof Error
					? error.message
					: 'An unexpected error occurred. Please try again.'
			);
		}
	};

	const isFormLoading = isSubmitting;

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className={cn('space-y-5', className)}
			noValidate
		>
			{error && (
				<div
					className="rounded-lg glass-light p-4 text-sm text-red-400 border border-red-400/30"
					role="alert"
					aria-live="polite"
				>
					{error}
				</div>
			)}

			<div className="space-y-3">
				<Label htmlFor="email" className="text-white/90">
					Email
				</Label>
				<Input
					id="email"
					type="email"
					autoComplete="email"
					placeholder="Enter your email"
					disabled={isFormLoading}
					readOnly={!!defaultEmail}
					aria-invalid={!!errors.email}
					aria-describedby={errors.email ? 'email-error' : undefined}
					className={cn(
						'glass-light border-white/20 text-white placeholder:text-white/40 focus:border-white/40 h-12',
						defaultEmail && 'opacity-70 cursor-not-allowed'
					)}
					{...register('email')}
				/>
				{errors.email && (
					<p id="email-error" className="text-sm text-red-400">
						{errors.email.message}
					</p>
				)}
			</div>

			<div className="space-y-3">
				<Label htmlFor="password" className="text-white/90">
					Password
				</Label>
				<div className="relative">
					<Input
						id="password"
						type={showPassword ? 'text' : 'password'}
						autoComplete="new-password"
						placeholder="Create a password"
						disabled={isFormLoading}
						aria-invalid={!!errors.password}
						aria-describedby={
							errors.password
								? 'password-error password-help'
								: 'password-help'
						}
						className="glass-light border-white/20 text-white placeholder:text-white/40 focus:border-white/40 pr-12 h-12"
						{...register('password')}
					/>
					<button
						type="button"
						className="absolute inset-y-0 right-0 flex items-center pr-4 text-white/60 hover:text-white transition-colors"
						onClick={() => setShowPassword(!showPassword)}
						disabled={isFormLoading}
						aria-label={
							showPassword ? 'Hide password' : 'Show password'
						}
					>
						{showPassword ? (
							<EyeOff className="h-5 w-5" />
						) : (
							<Eye className="h-5 w-5" />
						)}
					</button>
				</div>
				<div id="password-help" className="text-xs text-white/40">
					Password must be at least 8 characters with uppercase,
					lowercase, number, and special character.
				</div>
				{errors.password && (
					<p id="password-error" className="text-sm text-red-400">
						{errors.password.message}
					</p>
				)}
			</div>

			<div className="space-y-3">
				<Label htmlFor="confirmPassword" className="text-white/90">
					Confirm Password
				</Label>
				<div className="relative">
					<Input
						id="confirmPassword"
						type={showConfirmPassword ? 'text' : 'password'}
						autoComplete="new-password"
						placeholder="Confirm your password"
						disabled={isFormLoading}
						aria-invalid={!!errors.confirmPassword}
						aria-describedby={
							errors.confirmPassword
								? 'confirm-password-error'
								: undefined
						}
						className="glass-light border-white/20 text-white placeholder:text-white/40 focus:border-white/40 pr-12 h-12"
						{...register('confirmPassword')}
					/>
					<button
						type="button"
						className="absolute inset-y-0 right-0 flex items-center pr-4 text-white/60 hover:text-white transition-colors"
						onClick={() =>
							setShowConfirmPassword(!showConfirmPassword)
						}
						disabled={isFormLoading}
						aria-label={
							showConfirmPassword
								? 'Hide password'
								: 'Show password'
						}
					>
						{showConfirmPassword ? (
							<EyeOff className="h-5 w-5" />
						) : (
							<Eye className="h-5 w-5" />
						)}
					</button>
				</div>
				{errors.confirmPassword && (
					<p
						id="confirm-password-error"
						className="text-sm text-red-400"
					>
						{errors.confirmPassword.message}
					</p>
				)}
			</div>

			<Button
				type="submit"
				className="w-full glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30 h-12 text-base mt-6"
				disabled={isFormLoading}
				aria-describedby={error ? 'form-error' : undefined}
			>
				{isFormLoading && (
					<Loader2 className="mr-2 h-5 w-5 animate-spin" />
				)}
				Create Account
			</Button>

			<div className="text-center pt-2">
				<span className="text-sm text-white/60">
					Already have an account?{' '}
					<Link
						href="/login"
						className="text-white font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-white/20 rounded px-1"
					>
						Sign in
					</Link>
				</span>
			</div>
		</form>
	);
}
